import { useRef, useState, useCallback, RefObject } from 'react';
import { useLoader, ThreeEvent, useFrame, useThree } from '@react-three/fiber';
import { Bvh, Html, CameraControls, TransformControls } from '@react-three/drei';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { TextureLoader, Texture, Group, Mesh, MeshLambertMaterial, DoubleSide, SRGBColorSpace, MathUtils, Matrix3, Object3D, Vector3 } from 'three';
import { SubjectDraft, LabelDraft } from '@/contexts';
import { AreaConfig } from '#server/models';
import { AxesLabel } from '@/components/ThreeDeeViewer/Experience/Subject/AxesLabel';
import { useTransformDrag } from '../hooks/useTransformDrag';

// How far to push a freshly-placed label above the mesh surface (world units).
// Prevents the occlude depth-test from z-fighting at the exact surface boundary.
const SURFACE_OFFSET = 0.5;

interface AdminSubjectProps {
  draft: SubjectDraft;
  areaConfig?: AreaConfig;
  onMeshClick?: (position: [number, number, number]) => void;
  onLabelClick?: (labelId: string) => void;
  labels?: LabelDraft[];
  placingLabelId?: string | null;
  activeLabelId?: string | null;
  showAxes?: boolean;
  forceVisible?: boolean;
  transformMode?: 'translate' | 'rotate' | 'scale' | null;
  onTransformChange?: (t: { position: [number, number, number]; rotation: [number, number, number]; scale: [number, number, number] }) => void;
}

const resolveLabelColor = (label: LabelDraft, areaConfig?: AreaConfig): string => {
  if (!areaConfig || !label.primaryCategoryId) return '#888888';
  const group = areaConfig.categoryGroups.find((g) => g.id === label.primaryCategoryId!.groupId);
  const cat = group?.categories.find((c) => c.id === label.primaryCategoryId!.id);
  return cat?.color ?? '#888888';
};

const AdminLabelDot = ({
  label,
  color,
  isPlacing,
  isSelected,
  onClick,
  subjectRef,
  labelVisibleDist,
  labelVisibleRange,
  forceVisible,
}: {
  label: LabelDraft;
  color: string;
  isPlacing: boolean;
  isSelected: boolean;
  onClick: () => void;
  subjectRef: RefObject<Object3D | null>;
  labelVisibleDist: number;
  labelVisibleRange: number;
  forceVisible: boolean;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const htmlRef = useRef<HTMLDivElement>(null);
  const { controls } = useThree() as { controls: CameraControls | null };

  useFrame(() => {
    const el = htmlRef.current?.parentElement;
    if (!el) return;

    const dist = (controls as CameraControls)?.distance ?? 0;
    const opacity = forceVisible
      ? 1
      : Math.max(0, Math.min(1, (labelVisibleDist - dist) / Math.max(1, labelVisibleRange)));
    el.style.opacity = String(opacity);
    el.style.pointerEvents = opacity > 0.01 ? 'auto' : 'none';

    if (isHovered || isSelected || isPlacing) {
      el.style.zIndex = '16777215';
    }
  });

  const dotColor = isPlacing ? '#F59E0B' : color;
  const showName = isHovered || isSelected;

  return (
    <Html
      ref={htmlRef}
      position={label.config3D.position}
      occlude={!forceVisible && subjectRef.current ? [subjectRef as RefObject<Object3D>] : []}
    >
      <div
        onMouseOver={() => setIsHovered(true)}
        onMouseOut={() => setIsHovered(false)}
        onFocus={() => setIsHovered(true)}
        onBlur={() => setIsHovered(false)}
      >
        <button
          className="h-2.5 w-2.5 border-2 border-white cursor-pointer pointer-events-auto transform -translate-x-1/2 -translate-y-1/2 rounded-full transition-colors duration-200 ease-in"
          style={{ backgroundColor: dotColor }}
          aria-label={label.title || '(untitled)'}
          onClick={onClick}
        />
        <button
          className={`${showName ? 'block' : 'hidden'} pointer-events-auto text-white -mt-3 ml-1 bg-black border-[3px] whitespace-nowrap cursor-pointer bg-opacity-65`}
          style={{
            borderColor: isSelected ? dotColor : 'rgba(0,0,0,0.65)',
            fontSize: '0.75rem',
            paddingLeft: '1.1rem',
            paddingRight: '1.1rem',
            paddingTop: '0.25rem',
            paddingBottom: '0.25rem',
            borderRadius: '0.85rem',
          }}
          onClick={onClick}
        >
          {label.title || '(untitled)'}
        </button>
      </div>
    </Html>
  );
};

// ─── Scene body — shared between textured and untextured variants ─────────────

interface AdminSubjectBodyProps extends AdminSubjectProps {
  model: Group;
  texture: Texture | null;
}

const AdminSubjectBody = ({ draft, areaConfig, model, texture, onMeshClick, onLabelClick, labels, placingLabelId, activeLabelId, showAxes = true, forceVisible = false, transformMode, onTransformChange }: AdminSubjectBodyProps) => {
  const { config3D } = draft;
  const subjectRef = useRef<Object3D | null>(null);
  // Callback ref: setting a plain ref doesn't trigger a re-render, so on first
  // mount the labels would render with subjectRef.current = null and occlude=[].
  // This state bump forces a re-render once the model mounts, so occlude is
  // correctly populated before the first frame is displayed.
  const [, setSubjectMounted] = useState(false);
  const subjectRefCallback = useCallback((node: Object3D | null) => {
    subjectRef.current = node;
    if (node) setSubjectMounted(true);
  }, []);
  const transformGroupRef = useRef<Group>(null);
  const transformControlsRef = useRef<any>(null);
  const { controls } = useThree() as { controls: CameraControls | null };
  const controlsRef = useRef<CameraControls | null>(null);
  controlsRef.current = controls;

  model.traverse((child) => {
    if (child.type === 'Mesh') {
      (child as Mesh).material = new MeshLambertMaterial({ side: DoubleSide, map: texture ?? undefined });
    }
  });

  const handleClick = (event: ThreeEvent<MouseEvent>) => {
    if (!onMeshClick || !subjectRef.current) return;
    event.stopPropagation();

    // Start from the exact hit point, then nudge along the outward face normal
    // by SURFACE_OFFSET world units. Without this, the label sits exactly on the
    // mesh surface and the occlude raycaster z-fights at the boundary, making the
    // dot flicker. A small outward offset guarantees the dot is unambiguously in
    // front of the surface when viewed from the front, and behind when viewed from
    // the back — exactly the occlusion behaviour we want.
    const worldPos = event.point.clone();
    if (event.face) {
      const normalMatrix = new Matrix3().getNormalMatrix(event.object.matrixWorld);
      const worldNormal = event.face.normal.clone().applyMatrix3(normalMatrix).normalize();
      worldPos.addScaledVector(worldNormal, SURFACE_OFFSET);
    }

    const localPos = worldPos.clone();
    subjectRef.current.worldToLocal(localPos);
    onMeshClick([localPos.x, localPos.y, localPos.z]);
  };

  useTransformDrag(transformControlsRef, controlsRef, transformGroupRef.current, onTransformChange, transformMode);

  const transformGroup = transformGroupRef.current;

  return (
    <>
      <Bvh>
        <group
          ref={transformGroupRef}
          rotation={[
            MathUtils.degToRad(config3D.rotation[0]),
            MathUtils.degToRad(config3D.rotation[1]),
            MathUtils.degToRad(config3D.rotation[2])
          ]}
          position={config3D.position}
          scale={config3D.scale}
        >
          <ambientLight color={0xffffff} intensity={config3D.ambientLight} />
          <primitive object={model} ref={subjectRefCallback} onClick={onMeshClick ? handleClick : undefined} />
          {labels?.map((label) => (
            <AdminLabelDot
              key={label._id}
              label={label}
              color={resolveLabelColor(label, areaConfig)}
              isPlacing={label._id === placingLabelId}
              isSelected={activeLabelId === label._id}
              onClick={() => onLabelClick?.(label._id)}
              subjectRef={subjectRef}
              labelVisibleDist={config3D.labelVisibleDist}
              labelVisibleRange={config3D.labelVisibleRange}
              forceVisible={forceVisible}
            />
          ))}
        </group>
      </Bvh>
      {showAxes && config3D.axes && (
        <AxesLabel cameraControlsRef={controlsRef} axesLabels={config3D.axesLabels} />
      )}
      {transformMode && transformGroup && (
        <TransformControls ref={transformControlsRef} object={transformGroup} mode={transformMode} />
      )}
    </>
  );
};

// ─── Variants that conditionally call useLoader(TextureLoader) ────────────────

const AdminSubjectTextured = (props: AdminSubjectProps & { textureUrl: string }) => {
  const model = useLoader(FBXLoader, props.draft.config3D._modelBlobUrl!) as Group;
  const texture = useLoader(TextureLoader, props.textureUrl);
  texture.colorSpace = SRGBColorSpace;
  return <AdminSubjectBody {...props} model={model} texture={texture} />;
};

const AdminSubjectPlain = (props: AdminSubjectProps) => {
  const model = useLoader(FBXLoader, props.draft.config3D._modelBlobUrl!) as Group;
  return <AdminSubjectBody {...props} model={model} texture={null} />;
};

// ─── Public component ─────────────────────────────────────────────────────────

export const AdminSubject = (props: AdminSubjectProps) => {
  const textureUrl = props.draft.config3D._textureBlobUrl;
  return textureUrl
    ? <AdminSubjectTextured {...props} textureUrl={textureUrl} />
    : <AdminSubjectPlain {...props} />;
};
